// Cốt truyện: tiến độ mở đầu (STORY Phụ lục A) và các mục Điển Tịch đã ghi. core.story từ save v3.
import { signal, computed, batch } from '@preact/signals';
import {
  OPENING_END,
  OPENING_STEPS,
  START_KIT,
  START_KIT_KEY,
  openingIndex,
  type OpeningStep,
} from '@data/content/opening';
import { FLAGS_P0, isValidFlagValue } from '@data/flags';
import { isCodexId } from '@data/content/codex';
import { isPlainObject } from '@save/types';
import { registerSlice } from '../registry';
import { now } from '../clock';
import { markDirty } from '../persistence';
import { grantReward } from '../rewards';
import { setFlag } from './ledger';
import { logLine } from './log';
import { beginCultivation } from './cultivation';
import { selectScreen, selectTab } from './ui';

export interface StoryState {
  opening: { beat: string; done: boolean };
  codex: string[];
}

function defaultStory(): StoryState {
  return { opening: { beat: OPENING_STEPS[0]?.id ?? OPENING_END, done: false }, codex: [] };
}

const _story = signal<StoryState>(defaultStory());

export const story = computed(() => _story.value);
export const codexEntries = computed(() => _story.value.codex);
/** Bước mở đầu đang hiện, hoặc null khi đã xong. */
export const openingStep = computed<OpeningStep | null>(() => {
  const o = _story.value.opening;
  if (o.done) return null;
  return OPENING_STEPS[openingIndex(o.beat)] ?? null;
});

function applyEffects(step: OpeningStep): void {
  const e = step.effects;
  if (!e) return;
  for (const [id, v] of Object.entries(e.flags ?? {})) setFlag(id, v);
  if (e.formLampRoot) beginCultivation(now());
  if (e.codex) unlockCodex(e.codex);
  if (e.reward)
    grantReward('quest', [{ kind: 'currency', id: 'spiritStone', amount: e.reward.spiritStone }], {
      key: `quest.${e.reward.questId}`,
      log: { channel: 'cultivation', key: `log.quest.${e.reward.questId}` },
    });
  if (e.startKit)
    grantReward(
      'story',
      START_KIT.map((k) => ({ kind: 'goods' as const, key: k.stack, amount: k.amount })),
      { key: START_KIT_KEY, log: { channel: 'cultivation', key: 'log.gift.startKit' } },
    );
}

/** Qua bước hiện tại (chạm / giữ xong / chọn). `choice` là giá trị của lựa chọn khi bước là 'choice'. */
export function advanceOpening(choice?: string): void {
  const step = openingStep.peek();
  if (!step) return;
  const input = step.input;
  if (input.kind === 'choice') {
    const def = FLAGS_P0.find((f) => f.id === input.flag);
    if (!def || !isValidFlagValue(def, choice)) return;
  }
  batch(() => {
    if (input.kind === 'choice' && choice !== undefined) setFlag(input.flag, choice);
    applyEffects(step);
    const next = OPENING_STEPS[openingIndex(step.id) + 1];
    if (next) {
      _story.value = { ..._story.value, opening: { beat: next.id, done: false } };
    } else {
      _story.value = { ..._story.value, opening: { beat: OPENING_END, done: true } };
      logLine('cultivation', 'log.cult.firstSeclusion');
      selectTab('character');
      selectScreen('cultivation');
    }
  });
  markDirty();
}

/** Bỏ qua mở đầu (kịch bản đo hiệu năng / e2e `?story=skip`): áp đủ hiệu ứng của mọi bước còn lại. */
export function skipOpening(): void {
  for (let i = 0; i < OPENING_STEPS.length && openingStep.peek(); i++) {
    const s = openingStep.peek();
    advanceOpening(
      s?.input.kind === 'choice' ? s.input.options[s.input.options.length - 1]?.value : undefined,
    );
  }
}

export function unlockCodex(ids: readonly string[]): void {
  const add = ids.filter((id) => isCodexId(id) && !_story.value.codex.includes(id));
  if (add.length === 0) return;
  batch(() => {
    _story.value = { ..._story.value, codex: [..._story.value.codex, ...add] };
    for (const id of add) logLine('fate', 'log.codex.new', { entry: `@${id}.title` });
  });
}

registerSlice<StoryState>({
  key: 'story',
  scope: 'core',
  serialize: () => _story.value,
  hydrate: (d) => {
    const st = defaultStory();
    if (isPlainObject(d)) {
      const o = isPlainObject(d.opening) ? d.opening : {};
      if (o.done === true) st.opening = { beat: OPENING_END, done: true };
      else if (typeof o.beat === 'string' && openingIndex(o.beat) >= 0)
        st.opening = { beat: o.beat, done: false };
      if (Array.isArray(d.codex)) st.codex = d.codex.filter(isCodexId);
    }
    _story.value = st;
  },
});
